import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useWallet } from "@/hooks/useWallet";
import {
  getMembershipLabel,
  getMembershipStyle,
  canAccessVIP,
} from "@/lib/membership";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WalletBalanceCard from "./components/WalletBalanceCard";

/* ── Small helper: editable row ─────────────────── */
function FieldRow({
  label,
  value,
  isEditing,
  editValue,
  readOnly,
  onEdit,
  onSave,
  onCancel,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  isEditing: boolean;
  editValue: string;
  readOnly?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-stone-50 last:border-0">
      <span className="font-body text-sm text-stone-500 shrink-0">{label}</span>
      <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type={type}
              value={editValue}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              className="font-body text-sm px-3 py-1.5 rounded-lg border border-stone-200 focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none w-40 sm:w-56"
              autoFocus
            />
            <button
              onClick={onSave}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
            >
              <i className="ri-check-line text-sm" />
            </button>
            <button
              onClick={onCancel}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-stone-50 text-stone-400 hover:bg-stone-100 transition-colors"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <>
            <span className="font-body text-sm text-stone-800 truncate">
              {value || <span className="text-stone-300">未填寫</span>}
            </span>
            {!readOnly && (
              <button
                onClick={onEdit}
                className="w-7 h-7 flex items-center justify-center rounded-full text-stone-400 hover:text-rose-700 hover:bg-rose-50 transition-colors"
              >
                <i className="ri-edit-line text-sm" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Stat Card ──────────────────────────────────── */
function StatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: string;
  icon: string;
  accent: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
      <div className={`w-10 h-10 rounded-lg ${accent} flex items-center justify-center mx-auto mb-2`}>
        <i className={`${icon} text-lg`} />
      </div>
      <p className="font-display text-lg font-bold text-stone-800">{value}</p>
      <p className="font-body text-[11px] text-stone-400 mt-0.5">{label}</p>
    </div>
  );
}

/* ── Skeleton helper ───────────────────────────── */
function SkeletonPulse({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-stone-200 rounded ${className}`} />;
}

/* ── Main Profile Page ──────────────────────────── */
export default function ProfilePage() {
  const { user, profile, isLoggedIn, signOut, getUserIdentifier, updateProfile } = useAuth();
  const navigate = useNavigate();
  const { wallet, loading: walletLoading } = useWallet();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState("");

  const displayId = getUserIdentifier() || user?.email || "會員";
  const isVIP = canAccessVIP(profile?.membership_level);
  const levelLabel = getMembershipLabel(profile?.membership_level);
  const levelStyle = getMembershipStyle(profile?.membership_level);

  const startEdit = useCallback((field: string, currentValue: string) => {
    setEditingField(field);
    setEditValue(currentValue || "");
    setSaveError("");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setEditValue("");
    setSaveError("");
  }, []);

  const handleSave = useCallback(
    async (field: string) => {
      if (!editValue.trim()) {
        setSaveError("欄位不可為空");
        return;
      }
      setSaveLoading(true);
      setSaveError("");
      try {
        const updates: Record<string, unknown> = { [field]: editValue.trim() };
        await updateProfile(updates);
        setEditingField(null);
        setEditValue("");
      } catch {
        setSaveError("儲存失敗，請重試");
      } finally {
        setSaveLoading(false);
      }
    },
    [editValue, updateProfile]
  );

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // ── Not logged in ──
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <main className="pt-32 px-4 flex items-center justify-center">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-line text-stone-400 text-2xl" />
            </div>
            <p className="font-body text-base text-stone-600 mb-6">您尚未登入</p>
            <Link
              to="/?showLogin=1"
              className="inline-block font-body text-sm font-semibold px-6 py-2.5 rounded-full bg-rose-800 text-white hover:bg-rose-900 transition-colors whitespace-nowrap"
            >
              前往登入
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const regDate = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("zh-TW")
    : "—";

  const statusMap: Record<string, { label: string; color: string; bg: string }> = {
    active: { label: "正常", color: "text-emerald-700", bg: "bg-emerald-50" },
    suspended: { label: "暫停", color: "text-amber-700", bg: "bg-amber-50" },
    banned: { label: "封禁", color: "text-red-600", bg: "bg-red-50" },
    frozen: { label: "凍結", color: "text-stone-500", bg: "bg-stone-100" },
  };
  const statusInfo = statusMap[profile?.account_status || "active"] || statusMap.active;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="font-display text-xl md:text-2xl font-bold text-stone-800">
                會員資料
              </h1>
              <p className="font-body text-sm text-stone-500 mt-1">
                查看與管理你的個人資料與帳戶設定
              </p>
            </div>

            {/* ── Overview Card ── */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden mb-5">
              <div className="p-6 flex flex-col sm:flex-row items-center sm:items-start gap-5">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-rose-800 flex items-center justify-center">
                    <span className="font-display text-2xl font-bold text-white">
                      {displayId.slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  {isVIP && (
                    <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center border-2 border-white">
                      <i className="ri-vip-crown-line text-white text-xs" />
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left flex-1">
                  {profile ? (
                    <>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <h2 className="font-display text-lg font-bold text-stone-800">
                          {profile.name || displayId}
                        </h2>
                        <span className={`font-body text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusInfo.bg} ${statusInfo.color} ${statusInfo.bg.replace("bg-", "border-")}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <p className="font-body text-sm text-stone-500 mt-1">
                        帳號：<span className="text-stone-700">{displayId}</span>
                      </p>
                      <p className="font-body text-xs text-stone-400 mt-0.5">
                        註冊日期：{regDate}
                      </p>
                      <div className="flex items-center gap-2 mt-3 justify-center sm:justify-start">
                        <span className={`font-body text-[11px] font-semibold px-2.5 py-1 rounded-full border ${levelStyle}`}>
                          {levelLabel}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <SkeletonPulse className="h-5 w-32" />
                        <SkeletonPulse className="h-4 w-12" />
                      </div>
                      <p className="font-body text-sm text-stone-500 mt-1">
                        帳號：<span className="text-stone-700">{displayId}</span>
                      </p>
                      <SkeletonPulse className="h-3 w-24 mt-1" />
                      <SkeletonPulse className="h-4 w-20 mt-3" />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── Financial Summary ── */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <WalletBalanceCard />
              {profile && !walletLoading ? (
                <StatCard
                  label="累計收益"
                  value={`NT$ ${(wallet?.total_earnings ?? 0).toLocaleString()}`}
                  icon="ri-coins-line"
                  accent="bg-emerald-50 text-emerald-600"
                />
              ) : (
                <div className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-coins-line text-lg text-emerald-600" />
                  </div>
                  <SkeletonPulse className="h-6 w-24 mx-auto mb-2" />
                  <p className="font-body text-[11px] text-stone-400 mt-0.5">累計收益</p>
                </div>
              )}
              {profile && !walletLoading ? (
                <StatCard
                  label="待結算利潤"
                  value={`NT$ ${(wallet?.pending_profits ?? 0).toLocaleString()}`}
                  icon="ri-time-line"
                  accent="bg-amber-50 text-amber-600"
                />
              ) : (
                <div className="bg-white rounded-2xl border border-stone-200 p-5 text-center">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-2">
                    <i className="ri-time-line text-lg text-amber-600" />
                  </div>
                  <SkeletonPulse className="h-6 w-24 mx-auto mb-2" />
                  <p className="font-body text-[11px] text-stone-400 mt-0.5">待結算利潤</p>
                </div>
              )}
            </div>

            {/* Save error banner */}
            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 mb-5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-error-warning-line text-red-500 text-sm" />
                </div>
                <p className="font-body text-xs text-red-600">{saveError}</p>
              </div>
            )}

            {/* ── Basic Info Card ── */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden mb-5">
              <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
                <h3 className="font-display text-base font-bold text-stone-800">
                  <i className="ri-user-settings-line text-rose-700 mr-1.5" />
                  基本資料
                </h3>
                {saveLoading && (
                  <span className="font-body text-xs text-stone-400 flex items-center gap-1">
                    <i className="ri-loader-4-line animate-spin" />
                    儲存中...
                  </span>
                )}
              </div>
              {profile ? (
                <div className="px-6 py-2">
                  <FieldRow
                    label="姓名"
                    value={profile.name || ""}
                    isEditing={false}
                    editValue=""
                    readOnly
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onChange={() => {}}
                    placeholder="請輸入姓名"
                  />
                  <FieldRow
                    label="手機號碼"
                    value={profile.phone || ""}
                    isEditing={false}
                    editValue=""
                    readOnly
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onChange={() => {}}
                    placeholder="請輸入手機號碼"
                    type="tel"
                  />
                  <FieldRow
                    label="帳號"
                    value={displayId}
                    isEditing={false}
                    editValue=""
                    readOnly
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onChange={() => {}}
                  />
                  <FieldRow
                    label="註冊日期"
                    value={regDate}
                    isEditing={false}
                    editValue=""
                    readOnly
                    onEdit={() => {}}
                    onSave={() => {}}
                    onCancel={() => {}}
                    onChange={() => {}}
                  />
                </div>
              ) : (
                <div className="px-6 py-4 space-y-3">
                  <SkeletonPulse className="h-4 w-full" />
                  <SkeletonPulse className="h-4 w-3/4" />
                  <SkeletonPulse className="h-4 w-1/2" />
                  <SkeletonPulse className="h-4 w-2/3" />
                </div>
              )}
            </div>

            {/* ── Security Card ── */}
            <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden mb-5">
              <div className="px-6 py-4 border-b border-stone-100">
                <h3 className="font-display text-base font-bold text-stone-800">
                  <i className="ri-shield-check-line text-rose-700 mr-1.5" />
                  安全設定
                </h3>
              </div>
              {profile ? (
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                        <i className="ri-shield-keyhole-line text-stone-500" />
                      </div>
                      <div>
                        <p className="font-body text-sm font-medium text-stone-800">提款密碼</p>
                        <p className="font-body text-xs text-stone-400">
                          {profile.withdraw_password ? "已設置提款密碼" : "尚未設置提款密碼"}
                        </p>
                      </div>
                    </div>
                    <span className={`font-body text-[11px] font-semibold px-2.5 py-1 rounded-full border ${profile.withdraw_password ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}>
                      {profile.withdraw_password ? "已設置" : "未設置"}
                    </span>
                  </div>

                  <div className="h-px bg-stone-100 my-3" />

                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                        <i className="ri-logout-box-r-line text-stone-500" />
                      </div>
                      <div>
                        <p className="font-body text-sm font-medium text-stone-800">登出帳號</p>
                        <p className="font-body text-xs text-stone-400">退出當前會員帳號</p>
                      </div>
                    </div>
                    <button
                      onClick={handleSignOut}
                      className="font-body text-sm font-medium px-4 py-2 rounded-full border border-stone-300 text-stone-600 hover:bg-stone-50 hover:text-rose-700 hover:border-rose-200 transition-colors whitespace-nowrap"
                    >
                      登出
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-6 py-4 space-y-3">
                  <SkeletonPulse className="h-10 w-full" />
                  <div className="h-px bg-stone-100" />
                  <SkeletonPulse className="h-10 w-full" />
                </div>
              )}
            </div>

            {/* ── Quick Actions ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Link
                to="/withdrawal"
                className="flex items-center gap-3 bg-white rounded-2xl border border-stone-200 p-4 hover:border-rose-300 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
                  <i className="ri-money-cny-circle-line text-rose-600" />
                </div>
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-stone-800">提款中心</p>
                  <p className="font-body text-xs text-stone-400">申請提款與管理銀行資料</p>
                </div>
                <i className="ri-arrow-right-s-line text-stone-400" />
              </Link>
              <Link
                to="/dashboard#subscriptions"
                className="flex items-center gap-3 bg-white rounded-2xl border border-stone-200 p-4 hover:border-rose-300 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center">
                  <i className="ri-dashboard-line text-stone-600" />
                </div>
                <div className="flex-1">
                  <p className="font-body text-sm font-medium text-stone-800">認購中心</p>
                  <p className="font-body text-xs text-stone-400">查看認購記錄與收益</p>
                </div>
                <i className="ri-arrow-right-s-line text-stone-400" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}